import React, { useMemo, useState } from "react";
import Cookies from "js-cookie";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";
import useNotification from "../common/Notification";
import { createCreditPayment, confirmCreditPayment, paymentErrorMessage } from "./billingApi";
import { formatCreditCount, rupeesToPaisa } from "./billingFormat";
import { openRazorpayCheckout, readCheckoutFields } from "./razorpayCheckout";

/** Matches POST /api/payment amount validation in the API document. */
const MIN_RUPEES = 1;
const MAX_RUPEES = 1000000;

const BuyCreditsDialog = ({ open, onClose, onPaid }) => {
    const notify = useNotification();
    const [rupees, setRupees] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const amountNumber = useMemo(() => {
        if (rupees === "" || rupees == null) return null;
        const n = Number(rupees);
        return Number.isFinite(n) ? n : null;
    }, [rupees]);

    const credits = useMemo(
        () => (amountNumber == null ? null : rupeesToPaisa(amountNumber)),
        [amountNumber]
    );

    const amountError = useMemo(() => {
        if (rupees === "") return "";
        if (amountNumber == null) return "Enter a valid rupee amount.";
        if (amountNumber < MIN_RUPEES) return `Minimum amount is ₹${MIN_RUPEES}.`;
        if (amountNumber > MAX_RUPEES) {
            return `Maximum amount is ₹${MAX_RUPEES.toLocaleString("en-IN")}.`;
        }
        return "";
    }, [amountNumber, rupees]);

    const valid = amountNumber != null && !amountError && credits != null && credits > 0;

    const handleClose = () => {
        if (busy) return;
        setError("");
        onClose();
    };

    const handlePay = async () => {
        if (!valid) {
            setError(amountError || "Enter an amount between ₹1 and ₹10,00,000.");
            return;
        }
        if (!String(process.env.REACT_APP_RAZORPAY_KEY_ID || "").trim()) {
            const message = "Payment could not be started. Add REACT_APP_RAZORPAY_KEY_ID to the app environment.";
            setError(message);
            notify(message, "error");
            return;
        }

        setBusy(true);
        setError("");
        try {
            const payment = await createCreditPayment(amountNumber);
            const checkout = readCheckoutFields(payment);
            const credited = checkout.amountInRupees != null
                ? rupeesToPaisa(checkout.amountInRupees)
                : credits;
            const result = await openRazorpayCheckout({
                ...checkout,
                description: `Add ${formatCreditCount(credited)}`,
                name: Cookies.get("userName") || Cookies.get("name") || "",
                email: Cookies.get("email") || "",
            });
            await confirmCreditPayment(result);
            notify(`${formatCreditCount(credited)} added to the wallet`, "success");
            setRupees("");
            await onPaid?.();
            onClose();
        } catch (err) {
            const message = paymentErrorMessage(err, err?.message || "Payment could not be completed");
            if (message !== "Payment was cancelled") {
                setError(message);
                notify(message, "error");
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>Buy credits</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    1 credit equals 1 paisa. ₹100 adds 10,000 credits. The amount is charged in rupees and credited after payment is confirmed.
                </Typography>
                <TextField
                    autoFocus
                    fullWidth
                    label="Amount (₹)"
                    type="number"
                    value={rupees}
                    onChange={(e) => setRupees(e.target.value)}
                    error={Boolean(amountError)}
                    inputProps={{ min: MIN_RUPEES, max: MAX_RUPEES, step: "1" }}
                    helperText={
                        amountError
                        || (valid ? `You will receive ${formatCreditCount(credits)}` : `Enter ₹${MIN_RUPEES}–₹${MAX_RUPEES.toLocaleString("en-IN")}`)
                    }
                />
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={handlePay} disabled={busy || !valid}>
                    {busy ? "Processing…" : "Continue to pay"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BuyCreditsDialog;
